import { generateClient } from "aws-amplify/api";

export const client = generateClient();

// Helper function to handle GraphQL response parsing and error checking
export const handleGraphQLResponse = (response: any, operationName: string) => {
  // console.log(`Raw response data for ${operationName}:`, response);

  // Check if GraphQL response has errors
  if (response.errors && response.errors.length > 0) {
    const errorMessage = response.errors.map((e: any) => e.message).join(", ");
    throw new Error(`GraphQL errors: ${errorMessage}`);
  }

  // Handle case where response might already be parsed
  let result;
  if (typeof response === "string") {
    result = JSON.parse(response);
  } else {
    result = response;
  }
  // console.log(`Parsed result for ${operationName}:`, result);

  // Check if Lambda function returned an error
  if (
    (result.statusCode && result.statusCode >= 400) ||
    (result.body &&
      (JSON.parse(result.body).error || JSON.parse(result.body).user_error))
  ) {
    const errorMessage = (() => {
      if (result.body) {
        let temp = JSON.parse(result.body).error;
        if (temp) {
          return temp;
        }
      } else if (result.statusCode) {
        let statusCode = result.statusCode;
        if (statusCode === 500) {
          return "Unknown Internal Server Error";
        } else if (statusCode === 404) {
          return "Unknown Not Found Error";
        }
      }
      return "Unknown Error";
    })();

    const userErrorMessage = (() => {
      if (result.body) {
        let temp = JSON.parse(result.body).user_error;
        if (temp) {
          return temp;
        }
      } else if (result.statusCode) {
        let statusCode = result.statusCode;
        if (statusCode === 500) {
          return "Server Error";
        } else if (statusCode === 404) {
          return "Resource Not Found";
        }
      }
      return "Unknown Error";
    })();
    /* result.body
      ? JSON.parse(result.body).error || "Unknown error"
      : "Lambda function error";*/

    // Server error. Please try again later or contact support.
    console.error(`Lambda error (${result.statusCode}): ${errorMessage}`);

    throw new Error(userErrorMessage);
  }

  // Handle the nested body structure
  if (result.body) {
    const bodyData = JSON.parse(result.body);

    // Check if the parsed body contains an error
    if (bodyData.error) {
      throw new Error(`Data error: ${bodyData.error}`);
    }

    // Extract the appropriate data based on the operation
    switch (operationName) {
      case "customListPlants":
        return bodyData.plants || [];
      case "customListObjects":
        return bodyData.objects || [];
      case "customListProjects":
        return bodyData.projects || [];
      case "customListInventory":
        return bodyData.inventory || [];
      case "customPingInventory":
        return bodyData;
      case "customFetchPlant":
        return bodyData.plant || null;
      case "customGetObject":
        return bodyData.object || null;
      case "customGetProject":
        return bodyData.project || null;
      case "customGetProjectBoundary":
        return bodyData.XXX || null;
      case "customAddProject":
      case "customAddInventoryItem":
        return bodyData.data;
      case "customDeductInventoryQuantities":
        return bodyData.updated_items;
      case "customAddPlant":
      case "customUpdatePlant":
      case "customDeletePlant":
      case "customAddObject":
      case "customUpdateObject":
      case "customDeleteObject":
      case "customAddCompany":
      case "customUpdateCompany":
      case "customDeleteCompany":
      case "customUpdateProject":
      case "customDeleteProject":
      case "customUpdateProjectBoundary":
      case "customAddBoundaryPoint":
      case "customDeleteBoundaryPoint":
      case "customUpdateBoundaryPoint":
      case "customUpdateInventoryItem":
      case "customDeleteInventoryItem":
        // For mutations, return the full response body
        return bodyData;
      default:
        return bodyData;
    }
  }

  // Check for direct error
  if (result.error) {
    throw new Error(`Direct error: ${result.error}`);
  }

  return result;
};

// Generic CRUD operation handler
export const createCRUDHandler = (
  operationName: string,
  query: any,
  getVariables?: (params: any) => any
) => {
  return async (params: any) => {
    try {
      const variables = getVariables ? getVariables(params) : params;
      console.log("variables: ", variables);
      const response = await client.graphql({
        query,
        variables,
        authMode: "userPool",
      });

      // Type assertion to handle the response structure
      const responseData = (response as any).data?.[operationName];
      if (!responseData) {
        throw new Error(`No data returned for operation: ${operationName}`);
      }

      return handleGraphQLResponse(responseData, operationName);
    } catch (error) {
      console.error(`Error in ${operationName}:`, error);
      throw error;
    }
  };
};
